import React from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';

type IncidentDrawerTimelineTabProps = {
  incidentActivityError: string | null;
  incidentActivityLoading: boolean;
  incidentActivityItems: Array<any>;
  incidentComments: Array<any>;
  incidentCommentsLoading: boolean;
  incidentCommentsError: string | null;
  renderMode?: 'drawer' | 'page';
  siteTimeZone?: string | null;
};

const IncidentDrawerTimelineTab: React.FC<IncidentDrawerTimelineTabProps> = ({
  incidentActivityError,
  incidentActivityLoading,
  incidentActivityItems,
  incidentComments,
  incidentCommentsLoading,
  incidentCommentsError,
  renderMode = 'drawer',
  siteTimeZone = null,
}) => {
  const presentActivityError = (value: string | null): string | null => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const normalized = raw.toLowerCase();
    if (
      normalized === 'incident_activity_failed' ||
      normalized.includes('failed to load incident activity') ||
      normalized.includes('activity_failed')
    ) {
      return 'Review history is temporarily unavailable. You can still finish the review and retry the history panel later.';
    }
    return raw;
  };

  const activityErrorMessage = presentActivityError(incidentActivityError);
  const historyHeading = renderMode === 'page' ? 'Review history' : 'Action history';
  const commentsHeading = renderMode === 'page' ? 'Saved comments' : 'Operator comments';
  const presentWorkflowFields = (value: any): string | null => {
    const fields = Array.isArray(value) ? value.filter(Boolean).map((item) => String(item).replace(/_/g, ' ')) : [];
    if (!fields.length) return null;
    return `Workflow fields: ${fields.join(', ')}`;
  };
  const presentWorkflowFacts = (value: any): Array<{ label: string; value: string }> => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const label = String((item as any).label || '').trim();
        const factValue = String((item as any).value || '').trim();
        if (!label || !factValue) return null;
        return { label, value: factValue };
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {historyHeading}
      </Typography>
      {activityErrorMessage ? (
        <Alert severity="warning">{activityErrorMessage}</Alert>
      ) : incidentActivityLoading && incidentActivityItems.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">Loading review history...</Typography>
        </Box>
      ) : incidentActivityItems.length === 0 ? (
        <Alert severity="info">No review history has been recorded yet.</Alert>
      ) : (
        <>
          {incidentActivityItems.map((event, index) => (
            <Paper key={`${event.ts}-${event.kind}-${index}`} variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: '#fcfcfd' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {String(event?.title || '').trim() || 'Review activity recorded'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {event.ts ? formatSiteDateTime(event.ts, siteTimeZone) || event.ts : '—'}
              </Typography>
              {!!event.subtitle && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {event.subtitle}
                </Typography>
              )}
              {!!event.risk_disposition_label && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25, fontWeight: 700 }}>
                  {event.risk_disposition_label}
                </Typography>
              )}
              {!!presentWorkflowFields(event.workflow_fields) && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {presentWorkflowFields(event.workflow_fields)}
                </Typography>
              )}
              {presentWorkflowFacts(event.workflow_facts).map((fact) => (
                <Typography key={`${event.ts}-${fact.label}`} variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  {fact.label}: {fact.value}
                </Typography>
              ))}
              {event.non_production_mode ? (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                  Measured conditions: {String(event.source_context || 'special conditions')}
                </Typography>
              ) : null}
              {event.activity_type === 'outcome_measurement' && event.risk_reduction_claim_allowed === false ? (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25, fontWeight: 700 }}>
                  Outcome boundary: measured follow-up does not yet support a verified risk-reduction conclusion.
                </Typography>
              ) : null}
              {!!event.detail && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  {event.detail}
                </Typography>
              )}
            </Paper>
          ))}
        </>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
        {commentsHeading}
      </Typography>
      {incidentCommentsError ? (
        <Alert severity="warning">{incidentCommentsError}</Alert>
      ) : incidentCommentsLoading && incidentComments.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">Loading saved comments...</Typography>
        </Box>
      ) : incidentComments.length > 0 ? (
        incidentComments.slice(0, 8).map((comment, index) => (
          <Paper key={`${comment.id}-${comment.created_at || index}`} variant="outlined" sx={{ p: 1.25 }}>
            <Typography variant="caption" color="textSecondary">
              {comment.created_by || 'Operator'} • {comment.created_at ? formatSiteDateTime(comment.created_at, siteTimeZone) || comment.created_at : '—'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {comment.comment_text}
            </Typography>
          </Paper>
        ))
      ) : (
        <Alert severity="info">No review comments have been saved yet.</Alert>
      )}
    </Box>
  );
};

export default IncidentDrawerTimelineTab;
